import { createSelector } from 'redux-bundler';
import { snakeCase } from 'lodash';

import ImageDefinition from '../models/image_definition';
import ImagePipeline from '../models/image_pipeline';
import ImagePipelineRegion from '../models/image_pipeline_region';
import ImagePipelineSync from '../models/image_pipeline_sync';
import ImagePipelineSyncTransition from '../models/image_pipeline_sync_transition';
import ImagePipelineSyncCancellation from '../models/image_pipeline_sync_cancellation';

// import ImagePipelineSync from '../models/image_pipeline_sync';

const STALE_AFTER = 30000;

const cleanSubmittedValues = (values, images) => {
  // in the current settings,The back-end expects an ami identifier - string.
  // After submitting the form, values.sourceAmi store the name of an Image. We need to change it.
  // We use this image name to find the matching Image object and store its identifier in values.sourceAmi
  // if we don't find a matching image, we assume that the user has typed a public ami in the combobox (ArchAmiSelect)
  const existingAmi = images.find(i => i.name === values.sourceAmi);
  values.sourceAmi = existingAmi ? existingAmi.identifier : values.sourceAmi;
  return values;
};

export default {
  name: 'imagePipelines',
  getReducer: () => {
    const initialData = {
      data: null,
      syncData: null,
      sortDirection: 'ASC',
      sortBy: 'name',
      loading: false,
      activeImagePipeline: null,
      loadingActiveImagePipeline: null,
      loadingSyncs: null,
      lastSuccess: null,
      recentItemsLastFetchedAt: null,
      recentItemsLoading: false,
      recentItems: null,
    };

    return (state = initialData, { type, payload }) => {
      if (type === 'FETCH_IMAGE_PIPELINE_SYNCS_START') {
        return { ...state, loadingSyncs: true };
      }
      if (type === 'FETCH_IMAGE_PIPELINE_SYNCS_SUCCESS') {
        return {
          ...state,
          loadingSyncs: false,
          syncData: payload.results,
        };
      }
      if (type === 'FETCH_IMAGE_PIPELINE_START') {
        return { ...state, loadingActiveImagePipeline: true };
      }
      if (type === 'FETCH_IMAGE_PIPELINE_SUCCESS') {
        return {
          ...state,
          loadingActiveImagePipeline: false,
          activeImagePipeline: payload.result,
        };
      }
      if (type === 'FETCH_IMAGE_PIPELINES_START') {
        return { ...state, loading: true };
      }

      if (type === 'FETCH_IMAGE_PIPELINE_RECENT_ITEMS_START') {
        return { ...state, recentItemsLoading: true };
      }
      if (type === 'FETCH_IMAGE_PIPELINE_RECENT_ITEMS_SUCCESS') {
        return {
          ...state,
          recentItemsLoading: false,
          recentItems: payload.results,
          recentItemsLastFetchedAt: payload.time,
        };
      }
      if (type === 'FETCH_IMAGE_PIPELINES_SUCCESS') {
        return {
          ...state,
          loading: false,
          data: payload.results,
          lastSuccess: payload.time,
        };
      }
      if (type === 'CHANGE_IMAGE_PIPELINE_SORT') {
        return {
          ...state,
          sortDirection: payload.sortDirection,
          sortBy: payload.sortBy,
        };
      }
      if (type === 'ADD_IMAGE_PIPELINE') {
        return {
          ...state,
          data: state.data
            ? state.data.concat([payload.imagePipeline])
            : [payload.imagePipeline],
        };
      }
      if (type === 'UPDATE_IMAGE_PIPELINE') {
        if (state.data) {
          const index = state.data.findIndex(
            u => u.id === payload.imagePipeline.id,
          );
          const { imagePipeline } = payload;
          return {
            ...state,
            data: state.data
              .slice(0, index)
              .concat([imagePipeline])
              .concat(state.data.slice(index + 1)),
            activeImagePipeline:
              state.activeImagePipeline &&
              state.activeImagePipeline.id === payload.imagePipeline.id
                ? imagePipeline
                : state.activeImagePipeline,
          };
        }

        return {
          ...state,
          activeImagePipeline: payload.imagePipeline,
        };
      }
      if (type === 'TOGGLE_IMAGE_PIPELINE_SELECTION') {
        const index = state.data.findIndex(
          u => u.id === payload.imagePipeline.id,
        );
        const { imagePipeline } = payload;
        imagePipeline.selected = !imagePipeline.selected;
        imagePipeline.reset();
        return {
          ...state,
          data: state.data
            .slice(0, index)
            .concat([imagePipeline])
            .concat(state.data.slice(index + 1)),
        };
      }
      if (type === 'REMOVE_IMAGE_PIPELINE') {
        const index =
          state.data &&
          state.data.findIndex(u => u.id === payload.imagePipeline.id);
        return {
          ...state,
          data: state.data
            ? state.data.slice(0, index).concat(state.data.slice(index + 1))
            : null,
        };
      }

      if (type === 'RESET_CLIENT') {
        return {
          ...state,
          data: null,
        };
      }

      if (type === 'RESET_ACTIVE_FACILITY') {
        return {
          ...state,
          data: null,
        };
      }

      if (type === 'RESET_IMAGE_PIPELINE') {
        return {
          ...state,
          activeImagePipeline: null,
          syncData: null,
        };
      }

      return state;
    };
  },

  doResetImagePipeline: () => ({ dispatch }) => {
    dispatch({ type: 'RESET_IMAGE_PIPELINE' });
  },
  doFetchImagePipelineRecentItems: () => async ({
    dispatch,
    getState,
    store,
  }) => {
    const state = getState();
    const recentItems = store.selectRecentItems(state);
    const recentImagePipelines = recentItems.filter(
      i => i.type === 'ImagePipeline' && i.imagePipelineId,
    );

    const client = store.selectActiveClient(state);
    const facility = store.selectActiveFacility(state);

    dispatch({
      type: 'FETCH_IMAGE_PIPELINE_RECENT_ITEMS_START',
      facility: facility,
    });
    // console.log(
    //   'fetching image pipeline recent items for: ',
    //   recentImagePipelines,
    // );

    const response = { data: [] };
    await (async () => {
      for (const item of recentImagePipelines) {
        const imagePipeline = await ImagePipeline.includes({
          last_image_pipeline_sync: [
            'transitions',
            { image: ['approved_by'] },
            { images: ['approved_by', 'region'] },
            'performed_by',
            'chef_package_sync',
            'upstream_image_pipeline_sync',
            'image_definition',
          ],
        }).find(item.imagePipelineId);
        response.data.push(imagePipeline.data);
      }
    })();

    // console.log(response);
    // const response = await ImagePipeline.order({
    //   [snakeCase(sortBy)]: sortDirection.toLowerCase(),
    // })
    //   .includes([
    //     { facility: 'region' },
    //     'chef_package',
    //     'last_image_pipeline_sync',
    //     'instanceType',
    //     'imagePipelineRegions',
    //   ])
    //   .where({
    //     facility_id: facility.id,
    //   })
    //   .all();
    dispatch({
      type: 'FETCH_IMAGE_PIPELINE_RECENT_ITEMS_SUCCESS',
      payload: { results: response.data, time: Date.now() },
    });
  },

  doFetchImagePipelines: () => async ({ dispatch, getState, store }) => {
    const state = getState();
    const { sortBy } = state.imagePipelines;
    const { sortDirection } = state.imagePipelines;

    const client = store.selectActiveClient(state);
    const facility = store.selectActiveFacility(state);

    dispatch({ type: 'FETCH_IMAGE_PIPELINES_START', facility: facility });
    const response = await ImagePipeline.order({
      [snakeCase(sortBy)]: sortDirection.toLowerCase(),
    })
      .includes([
        { facility: 'region' },
        'chef_package',
        'last_image_pipeline_sync',
        'instanceType',
        'imagePipelineRegions',
      ])
      .where({
        facility_id: facility.id,
      })
      .all();
    dispatch({
      type: 'FETCH_IMAGE_PIPELINES_SUCCESS',
      payload: { results: response.data, time: Date.now() },
    });
  },

  doFetchImagePipelineSyncs: () => async ({ dispatch, getState, store }) => {
    const state = getState();

    const imagePipeline = store.selectActiveImagePipeline(state);

    dispatch({ type: 'FETCH_IMAGE_PIPELINE_SYNCS_START' });
    const response = await ImagePipelineSync.includes([
      'transitions',
      { image: ['approved_by'] },
      {
        images: 'region',
      },
      'performed_by',
      'chef_package_sync',
      'upstream_image_pipeline_sync',
      'image_definition',
    ])
      .where({
        image_pipeline_id: imagePipeline.id,
      })
      .all();
    dispatch({
      type: 'FETCH_IMAGE_PIPELINE_SYNCS_SUCCESS',
      payload: { results: response.data },
    });
  },

  doFetchActiveImagePipeline: imagePipelineId => async ({
    dispatch,
    getState,
    store,
  }) => {
    dispatch({ type: 'FETCH_IMAGE_PIPELINE_START' });

    const state = getState();
    const client = store.selectActiveClient(state);
    const facility = store.selectActiveFacility(state);

    const response = await ImagePipeline.includes([
      {
        last_image_pipeline_sync: [
          'transitions',
          { image: ['approved_by'] },
          { images: ['approved_by', 'region'] },
          'performed_by',
          'chef_package_sync',
          'upstream_image_pipeline_sync',
          'image_definition',
        ],
      },
      { facility: ['client', 'region'] },
      'instance_type',
      'keypair',
      'availability_zone',
      'vpc',
      'subnet',
      { chef_package: { cookbooks: 'recipes' } },
      { image_definition: 'created_by' },
      {
        source_image_pipeline: { facility: ['client', 'region'] },
      },
      'downstream_image_pipelines',
      'templates',

      {
        imagePipelineRegions: 'region',
      },
    ]).find(imagePipelineId);

    store.doAddRecentItem({
      name: response.data.name,
      imagePipelineId: imagePipelineId,
      facilityId: facility.id,
      clientId: client.id,
      secondary: `${client.name} / ${facility.name} (${facility.region.code})`,
      type: 'ImagePipeline',
      url: `/clients/${client.id}/facilities/${facility.id}/image_pipelines/${imagePipelineId}`,
    });
    dispatch({
      type: 'FETCH_IMAGE_PIPELINE_SUCCESS',
      payload: { result: response.data },
    });
  },

  doRefreshImagePipeline: () => async ({ store, getState }) => {
    const state = getState();
    const imagePipeline = store.selectActiveImagePipeline(state).dup();

    store.doFetchActiveImagePipeline(imagePipeline.id);
  },

  doRefreshImagePipeline2: imagePipeline => async ({ store, getState }) => {
    const state = getState();
    const imagePipeline = store.selectActiveImagePipeline(state).dup();

    store.doFetchActiveImagePipeline(imagePipeline.id);
  },

  doChangeImagePipelineSort: ({ sortDirection, sortBy }) => async ({
    dispatch,
    store,
  }) => {
    dispatch({
      type: 'CHANGE_IMAGE_PIPELINE_SORT',
      payload: { sortDirection, sortBy },
    });
    store.doFetchImagePipelines();
  },

  doToggleImagePipelineSelection: imagePipeline => ({ dispatch }) => {
    dispatch({
      type: 'TOGGLE_IMAGE_PIPELINE_SELECTION',
      payload: { imagePipeline },
    });
  },

  doCreateDownstreamImagePipeline: (imagePipeline, name) => async ({
    dispatch,
    getState,
    store,
  }) => {
    const state = getState();
    const facility = store.selectActiveFacility(state).dup();

    const response = await ImagePipeline.includes([
      { facility: ['client', 'region'] },
      'instance_type',
      'keypair',
      'availability_zone',
      'vpc',
      'subnet',
      { chef_package: { cookbooks: 'recipes' } },
      'client',
      {
        source_image_pipeline: { facility: ['client', 'region'] },
      },

      { imagePipelineRegions: 'region' },
    ]).find(imagePipeline.id);

    const imagePipelineWithAssociations = response.data;

    const attrs = Object.assign(
      {},
      imagePipelineWithAssociations.attributes,
      imagePipelineWithAssociations.relationships,
    );

    // strip createdAt
    delete attrs.createdAt;
    delete attrs.updatedAt;
    delete attrs.role;
    delete attrs.runListLength;
    const newImagePipeline = new ImagePipeline(attrs);

    newImagePipeline.name = name;
    newImagePipeline.customTags =
      imagePipelineWithAssociations.customTags || [];
    newImagePipeline.sourceImagePipeline = imagePipeline;
    newImagePipeline.imagePipelineRegions = imagePipelineWithAssociations.imagePipelineRegions.map(
      ipr => {
        const region = ipr.region;
        const newIpr = new ImagePipelineRegion({
          region: region,
        });
        return newIpr;
      },
    );

    const success = await newImagePipeline.save({
      with: [
        'facility.id',
        'chefPackage.id',
        'client.id',
        'keypair.id',
        'vpc.id',
        'availabilityZone.id',
        'instanceType.id',
        'subnet.id',
        'sourceImagePipeline.id',
        'imageDefinition',

        { imagePipelineRegions: 'region.id' },
      ],
    });

    if (success) {
      dispatch({ type: 'ADD_IMAGE_PIPELINE', payload: { imagePipeline } });
      store.doFetchImagePipelines();
    }

    return newImagePipeline;
  },

  doDuplicateImagePipeline: (imagePipeline, name) => async ({
    dispatch,
    getState,
    store,
  }) => {
    const state = getState();
    const facility = store.selectActiveFacility(state).dup();

    const response = await ImagePipeline.includes([
      { facility: ['client', 'region'] },
      'instance_type',
      'keypair',
      'availability_zone',
      'vpc',
      'subnet',
      { chef_package: { cookbooks: 'recipes' } },
      { image_definition: 'created_by' },
      'client',
      {
        source_image_pipeline: { facility: ['client', 'region'] },
      },

      { imagePipelineRegions: 'region' },
    ]).find(imagePipeline.id);

    const imagePipelineWithAssociations = response.data;

    const newImageDefinition = new ImageDefinition({
      runList: imagePipelineWithAssociations.imageDefinition.runList,
    });

    const attrs = Object.assign(
      {},
      imagePipelineWithAssociations.attributes,
      imagePipelineWithAssociations.relationships,
    );

    // strip createdAt
    delete attrs.createdAt;
    delete attrs.updatedAt;
    delete attrs.role;
    delete attrs.runListLength;
    const newImagePipeline = new ImagePipeline(attrs);

    newImagePipeline.name = name;
    newImagePipeline.imageDefinition = newImageDefinition;

    newImagePipeline.customTags =
      imagePipelineWithAssociations.customTags || [];
    newImagePipeline.imagePipelineRegions = imagePipelineWithAssociations.imagePipelineRegions.map(
      ipr => {
        const region = ipr.region;
        const newIpr = new ImagePipelineRegion({
          region: region,
        });
        return newIpr;
      },
    );

    const success = await newImagePipeline.save({
      with: [
        'facility.id',
        'chefPackage.id',
        'client.id',
        'keypair.id',
        'vpc.id',
        'availabilityZone.id',
        'instanceType.id',
        'subnet.id',
        'sourceImagePipeline.id',
        'imageDefinition',

        { imagePipelineRegions: 'region.id' },
      ],
    });

    if (success) {
      dispatch({ type: 'ADD_IMAGE_PIPELINE', payload: { imagePipeline } });

      store.doFetchImagePipelines();
    }
    return newImagePipeline;
  },

  doCreateImagePipeline: values => async ({ dispatch, store, getState }) => {
    const state = getState();
    const activeFacility = store.selectActiveFacility(state);
    const activeClient = store.selectActiveClient(state);
    const images = store.selectImages(state);

    values = cleanSubmittedValues(values, images);

    const iprsToCreate = (values.regions || []).map(
      region => new ImagePipelineRegion({ region: region }),
    );

    const imagePipeline = new ImagePipeline({
      facility: activeFacility,
      client: activeClient,

      imagePipelineRegions: iprsToCreate,
      ...values,
    });

    const success = await imagePipeline.save({
      with: [
        'facility.id',
        'chefPackage.id',
        'client.id',
        'keypair.id',
        'vpc.id',
        'availabilityZone.id',
        'instanceType.id',
        'subnet.id',
        'sourceImagePipeline.id',

        { imagePipelineRegions: 'region.id' },
      ],
    });
    if (success) {
      dispatch({ type: 'ADD_IMAGE_PIPELINE', payload: { imagePipeline } });
    }
    return imagePipeline;
  },

  doDestroyImagePipeline: imagePipeline => async ({ dispatch }) => {
    await imagePipeline.destroy();
    dispatch({ type: 'REMOVE_IMAGE_PIPELINE', payload: { imagePipeline } });
  },

  doUpdateImagePipeline: values => async ({ dispatch, store, getState }) => {
    const state = getState();

    const imagePipeline = store.selectActiveImagePipeline(state).dup();
    const images = store.selectImages(state);
    values = cleanSubmittedValues(values, images);

    const tsgasToDestroy = (imagePipeline.imagePipelineRegions || [])
      .filter(
        ({ region: { id } }) => !values.regions.map(sg => sg.id).includes(id),
      )
      .map(tsga => {
        const nextTsga = tsga;
        nextTsga.isMarkedForDestruction = true;
        return nextTsga;
      });

    const tsgasToCreate = values.regions
      .filter(
        ({ id }) =>
          !(imagePipeline.imagePipelineRegions || [])
            .map(({ region: sg }) => sg.id)
            .includes(id),
      )
      .map(sg => new ImagePipelineRegion({ region: sg }));

    if (
      values.sourceImagePipeline === null &&
      imagePipeline.sourceImagePipeline
    ) {
      imagePipeline.sourceImagePipeline.isMarkedForDisassociation = true;
      delete values.sourceImagePipeline;
    }
    const newValues = {
      ...values,
      imagePipelineRegions: [...tsgasToDestroy, ...tsgasToCreate],
    };
    imagePipeline.assignAttributes(newValues);
    // imagePipeline.imagePipelineRegions = [...tsgasToDestroy, ...tsgasToCreate];
    // console.log('New Values:');
    console.log(imagePipeline);
    // imagePipeline.assignAttributes({
    //   ...values,
    //   imagePipelineRegions: [...tsgasToDestroy, ...tsgasToCreate],
    // });

    // console.log('Regions:');
    // console.log(tsgasToCreate);
    // console.log(values);

    const success = await imagePipeline.save({
      with: [
        'chefPackage.id',
        'keypair.id',
        'vpc.id',
        'availabilityZone.id',
        'instanceType.id',
        'subnet.id',
        'sourceImagePipeline',

        { imagePipelineRegions: 'region.id' },
      ],
    });
    if (success) {
      dispatch({
        type: 'UPDATE_IMAGE_PIPELINE',
        payload: { imagePipeline },
      });

      store.doFetchActiveImagePipeline(imagePipeline.id);
    }
    return imagePipeline;
  },

  doSyncImagePipeline2: imagePipeline => async ({ getState, store }) => {
    const state = getState();
    const imagePipelineDup = imagePipeline.dup();

    const imagePipelineSync = new ImagePipelineSync({ imagePipeline });

    await imagePipelineSync.save({ with: ['imagePipeline.id'] });
    if (imagePipelineSync.isPersisted) {
      store.doFetchActiveImagePipeline(imagePipeline.id);
    }
  },

  doSyncImagePipeline: () => async ({ getState, store }) => {
    const state = getState();
    const imagePipeline = store.selectActiveImagePipeline(state).dup();

    const imagePipelineSync = new ImagePipelineSync({ imagePipeline });

    await imagePipelineSync.save({ with: ['imagePipeline.id'] });
    if (imagePipelineSync.isPersisted) {
      store.doFetchActiveImagePipeline(imagePipeline.id);
    }
  },

  doCancelImagePipelineSync: imagePipelineSync => async ({
    getState,
    store,
  }) => {
    const state = getState();

    const imagePipelineSyncCancellation = new ImagePipelineSyncCancellation({
      imagePipelineSync,
    });

    await imagePipelineSyncCancellation.save({
      with: ['imagePipelineSync.id'],
    });
  },

  doCreateImageDefinition: values => async ({ dispatch, getState, store }) => {
    const state = getState();
    const imagePipeline = store.selectActiveImagePipeline(state).dup();

    const imageDefinition = new ImageDefinition({
      runList: values.runList,
      imagePipeline: imagePipeline,
    });
    await imageDefinition.save({ with: ['imagePipeline.id'] });
    store.doRefreshImagePipeline();
  },

  selectImagePipelineSortBy: state => state.imagePipelines.sortBy,
  selectImagePipelineSortDirection: state => state.imagePipelines.sortDirection,
  selectImagePipelineLoading: state => state.imagePipelines.loading,
  selectImagePipelineRaw: state => state.imagePipelines,
  selectImagePipelineData: state => state.imagePipelines.data || [],

  selectImagePipelineRecentItems: state =>
    state.imagePipelines.recentItems || [],
  selectImagePipelineSyncs: state => state.imagePipelines.syncData || [],
  selectLastImagePipelinesSuccess: state => state.imagePipelines.lastSuccess,
  selectImagePipelinesAreStale: createSelector(
    'selectLastImagePipelinesSuccess',
    'selectAppTime',
    (lastSuccessTime, appTime) => {
      if (!lastSuccessTime) {
        return true;
      }
      return appTime - lastSuccessTime > STALE_AFTER;
    },
  ),

  selectImagePipelineRecentItemsLastFetchedAt: state =>
    state.imagePipelines.recentItemsLastFetchedAt,
  selectRecentItemsAreStale: createSelector(
    'selectImagePipelineRecentItemsLastFetchedAt',
    'selectAppTime',
    (recentItemsLastFetchedAt, appTime) => {
      if (!recentItemsLastFetchedAt) {
        return true;
      }
      return appTime - recentItemsLastFetchedAt > STALE_AFTER;
    },
  ),

  selectSelectedImagePipelineCount: createSelector(
    'selectImagePipelineData',
    imagePipelineData => imagePipelineData.filter(u => u.selected).length,
  ),
  selectSelectedImagePipelines: createSelector(
    'selectImagePipelineData',
    imagePipelineData => imagePipelineData.filter(u => u.selected),
  ),

  selectActiveImagePipelineId: createSelector(
    'selectRouteParams',
    routeParams => routeParams.imagePipelineId,
  ),
  selectActiveImagePipeline: createSelector(
    'selectImagePipelineRaw',
    imagePipelineData => imagePipelineData.activeImagePipeline,
  ),
  selectImagePipelineSyncId: createSelector(
    'selectRouteParams',
    routeParams => {
      console.log(routeParams);
      return routeParams.imagePipelineSyncId;
    },
  ),

  selectRecipeQueryParameter: createSelector(
    'selectQueryObject',
    queryObject => queryObject.recipe,
  ),

  reactShouldFetchImagePipelineRecentItems: createSelector(
    'selectImagePipelineRaw',
    'selectCurrentUser',
    'selectRecentItemsAreStale',
    (imagePipelineData, currentUser, isStale) => {
      if (imagePipelineData.recentItemsLoading || !currentUser) {
        return false;
      }

      if (imagePipelineData.recentItems && !isStale) return false;
      console.log('hello', isStale, imagePipelineData.recentItems?.length);
      return { actionCreator: 'doFetchImagePipelineRecentItems' };
    },
  ),
  reactShouldFetchImagePipelines: createSelector(
    'selectRouteApis',
    'selectImagePipelineRaw',
    'selectCurrentUser',
    'selectActiveClient',
    'selectActiveFacility',
    'selectImagePipelinesAreStale',
    (
      apis,
      imagePipelineData,
      currentUser,
      activeClient,
      activeFacility,
      isStale,
    ) => {
      const wantsImagePipelines = apis.includes('imagePipelines');
      if (
        !wantsImagePipelines ||
        imagePipelineData.loading ||
        !currentUser ||
        !activeClient ||
        !activeFacility
      ) {
        return false;
      }

      if (imagePipelineData.data && !isStale) return false;
      return { actionCreator: 'doFetchImagePipelines' };
    },
  ),
  reactShouldFetchImagePipelineSyncs: createSelector(
    'selectRouteApis',
    'selectImagePipelineRaw',
    'selectCurrentUser',
    'selectActiveClient',
    'selectActiveImagePipeline',
    (
      apis,
      imagePipelineData,
      currentUser,
      activeClient,
      activeImagePipeline,
    ) => {
      const wantsImagePipelineSyncs = apis.includes('imagePipelineSyncs');
      if (
        !activeImagePipeline ||
        !wantsImagePipelineSyncs ||
        imagePipelineData.loadingSyncs ||
        imagePipelineData.syncData ||
        !currentUser ||
        !activeClient
      ) {
        return false;
      }

      return { actionCreator: 'doFetchImagePipelineSyncs' };
    },
  ),
  reactShouldFetchActiveImagePipeline: createSelector(
    'selectRouteParams',
    'selectPathname',
    'selectActiveImagePipeline',
    'selectImagePipelineRaw',
    'selectActiveClient',
    'selectActiveFacility',
    (
      routeParams,
      pathname,
      activeImagePipeline,
      imagePipelineData,
      activeClient,
      activeFacility,
    ) => {
      if (!activeFacility) {
        return false;
      }

      if (!activeClient) {
        return false;
      }
      if (
        !pathname.includes('/image_pipelines') ||
        !routeParams.imagePipelineId ||
        imagePipelineData.loadingActiveImagePipeline
      ) {
        return null;
      }
      if (
        activeImagePipeline &&
        activeImagePipeline.id === routeParams.imagePipelineId
      ) {
        return null;
      }

      return {
        actionCreator: 'doFetchActiveImagePipeline',
        args: [routeParams.imagePipelineId],
      };
    },
  ),

  reactShouldResetImagePipeline: createSelector(
    'selectActiveImagePipelineId',
    'selectActiveImagePipeline',
    (activeImagePipelineId, activeImagePipeline) => {
      if (!activeImagePipeline) return null;
      if (activeImagePipeline.id !== activeImagePipelineId) {
        return { actionCreator: 'doResetImagePipeline' };
      }

      return null;
    },
  ),
};
